/**
 * Style Helper Utilities for YHYP Dashboard
 *
 * Centralized functions for generating consistent styling
 * across components, including risk indicators, status badges,
 * and semantic color mappings.
 */

import { AlertTriangle, CheckCircle, AlertCircle, XCircle, Info } from 'lucide-react';

// Risk Level Types
export type RiskLevel = 'low' | 'medium' | 'high';
export type StatusType = 'normal' | 'abnormal' | 'critical' | 'active' | 'discontinued';
export type SemanticType = 'success' | 'warning' | 'danger' | 'info';

/**
 * Get Tailwind color classes for risk levels
 */
export const getRiskColorClasses = (risk: RiskLevel): string => {
  const colorMap: Record<RiskLevel, string> = {
    low: 'bg-success-100 text-success-700 border-success-200',
    medium: 'bg-warning-100 text-warning-700 border-warning-200',
    high: 'bg-danger-100 text-danger-700 border-danger-200',
  };
  return colorMap[risk] || colorMap.low;
};

/**
 * Get hex color for risk levels (for charts and visualizations)
 */
export const getRiskColor = (risk: RiskLevel): string => {
  const colorMap: Record<RiskLevel, string> = {
    low: '#22c55e',   // success-500
    medium: '#f59e0b', // warning-500
    high: '#ef4444',   // danger-500
  };
  return colorMap[risk] || colorMap.low;
};

/**
 * Get icon component for risk levels
 */
export const getRiskIcon = (risk: RiskLevel) => {
  const iconMap: Record<RiskLevel, any> = {
    low: CheckCircle,
    medium: AlertTriangle,
    high: AlertCircle,
  };
  return iconMap[risk] || CheckCircle;
};

/**
 * Get Tailwind color classes for lab result status
 */
export const getStatusColorClasses = (status: StatusType): string => {
  const colorMap: Record<StatusType, string> = {
    normal: 'bg-success-100 text-success-700 border-success-200',
    abnormal: 'bg-warning-100 text-warning-700 border-warning-200',
    critical: 'bg-danger-100 text-danger-700 border-danger-200',
    active: 'bg-brand-dark-100 text-brand-dark-700 border-brand-dark-200',
    discontinued: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return colorMap[status] || colorMap.normal;
};

/**
 * Get hex color for status (for charts and visualizations)
 */
export const getStatusColor = (status: StatusType): string => {
  const colorMap: Record<StatusType, string> = {
    normal: '#22c55e',   // success-500
    abnormal: '#f59e0b', // warning-500
    critical: '#ef4444',  // danger-500
    active: '#6a6055',    // brand-dark-500
    discontinued: '#9ca3af', // gray-400
  };
  return colorMap[status] || colorMap.normal;
};

/**
 * Get icon component for status types
 */
export const getStatusIcon = (status: StatusType) => {
  const iconMap: Record<StatusType, any> = {
    normal: CheckCircle,
    abnormal: AlertTriangle,
    critical: XCircle,
    active: CheckCircle,
    discontinued: XCircle,
  };
  return iconMap[status] || CheckCircle;
};

/**
 * Get Tailwind classes for semantic color types
 */
export const getSemanticColorClasses = (type: SemanticType, variant: 'background' | 'text' | 'border' = 'background'): string => {
  const variantMap: Record<SemanticType, Record<string, string>> = {
    success: {
      background: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
    },
    warning: {
      background: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
    },
    danger: {
      background: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
    },
    info: {
      background: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
    },
  };
  return variantMap[type][variant] || '';
};

/**
 * Get hex color for semantic types
 */
export const getSemanticColor = (type: SemanticType, shade: number = 500): string => {
  const colorMap: Record<SemanticType, Record<number, string>> = {
    success: {
      100: '#dcfce7',
      200: '#bbf7d0',
      500: '#22c55e',
      700: '#15803d',
    },
    warning: {
      100: '#fef3c7',
      200: '#fde68a',
      500: '#f59e0b',
      700: '#b45309',
    },
    danger: {
      100: '#fee2e2',
      200: '#fecaca',
      500: '#ef4444',
      700: '#b91c1c',
    },
    info: {
      100: '#dbeafe',
      200: '#bfdbfe',
      500: '#3b82f6',
      700: '#1d4ed8',
    },
  };
  return colorMap[type][shade] || colorMap[type][500];
};

/**
 * Get icon component for semantic types
 */
export const getSemanticIcon = (type: SemanticType) => {
  const iconMap: Record<SemanticType, any> = {
    success: CheckCircle,
    warning: AlertTriangle,
    danger: XCircle,
    info: Info,
  };
  return iconMap[type] || Info;
};

/**
 * Generate a complete badge className string
 */
export const getBadgeClasses = (
  type: RiskLevel | StatusType | SemanticType,
  size: 'sm' | 'md' | 'lg' = 'md'
): string => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  // Determine which color function to use
  let colorClasses = '';
  if (['low', 'medium', 'high'].includes(type)) {
    colorClasses = getRiskColorClasses(type as RiskLevel);
  } else if (['normal', 'abnormal', 'critical', 'active', 'discontinued'].includes(type)) {
    colorClasses = getStatusColorClasses(type as StatusType);
  } else if (['success', 'warning', 'danger', 'info'].includes(type)) {
    const semanticType = type as SemanticType;
    colorClasses = `${getSemanticColorClasses(semanticType, 'background')} ${getSemanticColorClasses(semanticType, 'text')} ${getSemanticColorClasses(semanticType, 'border')}`;
  }

  return `inline-flex items-center rounded-full border font-medium ${sizeClasses[size]} ${colorClasses}`;
};

/**
 * Generate chart color array for multi-series data
 */
export const getChartColors = (count: number = 6): string[] => {
  const baseColors = [
    '#6a6055', // brand-dark
    '#b7a699', // brand-taupe
    '#3b82f6', // info
    '#22c55e', // success
    '#f59e0b', // warning
    '#ef4444', // danger
  ];

  // If we need more colors, cycle through with opacity variations
  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }

  const colors = [...baseColors];
  for (let i = baseColors.length; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }
  return colors;
};

/**
 * Calculate health metric risk level based on value and thresholds
 */
export const calculateRiskLevel = (
  value: number,
  normalRange: { min: number; max: number },
  criticalRange?: { min: number; max: number }
): RiskLevel => {
  // If within normal range
  if (value >= normalRange.min && value <= normalRange.max) {
    return 'low';
  }

  // If critical range is defined and value is in critical range
  if (criticalRange) {
    if (value < criticalRange.min || value > criticalRange.max) {
      return 'high';
    }
  }

  // Otherwise medium risk
  return 'medium';
};

/**
 * Format value with units for display
 */
export const formatMetricValue = (value: number, unit: string, decimals: number = 1): string => {
  return `${value.toFixed(decimals)} ${unit}`;
};

/**
 * Generate gradient backgrounds for cards
 */
export const getGradientBackground = (type: 'primary' | 'secondary' | 'accent' = 'primary'): string => {
  const gradients = {
    primary: 'bg-gradient-to-br from-brand-ivory-100 to-brand-beige-200',
    secondary: 'bg-gradient-to-br from-brand-taupe-50 to-brand-beige-100',
    accent: 'bg-gradient-to-br from-brand-dark-50 to-brand-taupe-100',
  };
  return gradients[type];
};

/**
 * Generate hover state classes
 */
export const getHoverClasses = (type: 'card' | 'button' = 'card'): string => {
  const hoverMap = {
    card: 'hover:shadow-lg hover:border-brand-taupe-300 transition-all duration-200',
    button: 'hover:opacity-90 active:opacity-80 transition-opacity duration-150',
  };
  return hoverMap[type];
};

/**
 * Get responsive container classes
 */
export const getContainerClasses = (size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'lg'): string => {
  const sizeMap = {
    sm: 'max-w-3xl',
    md: 'max-w-5xl',
    lg: 'max-w-7xl',
    xl: 'max-w-[1440px]',
    full: 'max-w-full',
  };
  return `${sizeMap[size]} mx-auto px-4 sm:px-6 lg:px-8`;
};

export default {
  getRiskColorClasses,
  getRiskColor,
  getRiskIcon,
  getStatusColorClasses,
  getStatusColor,
  getStatusIcon,
  getSemanticColorClasses,
  getSemanticColor,
  getSemanticIcon,
  getBadgeClasses,
  getChartColors,
  calculateRiskLevel,
  formatMetricValue,
  getGradientBackground,
  getHoverClasses,
  getContainerClasses,
};
